import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import apiService from "../services/api";
import { Button } from "../arc/components/button/button";
import { PasswordField } from "../arc/components/password-field/password-field";
import { PasswordStrength } from "../arc/components/password-strength/password-strength";
import { AXO_PASSWORD_RULES } from "../config/passwordRules";

const injectFont = () => {
  if (document.getElementById("dm-sans-font")) return;
  const link = document.createElement("link");
  link.id   = "dm-sans-font";
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap";
  document.head.appendChild(link);
};

const DM_SANS = "'DM Sans', system-ui, sans-serif";

const ResetPassword = () => {
  useEffect(() => { injectFont(); }, []);

  const [searchParams]      = useSearchParams();
  const navigate            = useNavigate();
  const token               = searchParams.get("token");

  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [status, setStatus]       = useState("idle"); // "idle" | "loading" | "done" | "error"
  const [error, setError]         = useState("");

  // If no token in URL, show an error immediately
  const invalidLink = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setStatus("loading");
    try {
      await apiService.resetPassword(token, password);
      setStatus("done");
    } catch (err) {
      setError(err.response?.data?.message || "This link is invalid or has expired.");
      setStatus("error");
    }
  };

  // ── shared input focus/blur handlers ──────────────────────────────────
  const onFocus = (e) => {
    e.target.style.border     = "1px solid rgba(131,148,171,0.8)";
    e.target.style.background = "rgba(255,255,255,0.1)";
    e.target.style.boxShadow  = "0 0 0 3px rgba(92,113,143,0.2)";
  };
  const onBlur = (e) => {
    e.target.style.border     = "1px solid rgba(92,113,143,0.45)";
    e.target.style.background = "rgba(255,255,255,0.06)";
    e.target.style.boxShadow  = "none";
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "#f1f2f4", fontFamily: DM_SANS, WebkitFontSmoothing: "antialiased" }}
    >
      {/* Ambient gradients */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 60%, rgba(12,44,84,0.08) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(92,113,143,0.1) 0%, transparent 45%), radial-gradient(circle at 50% 95%, rgba(131,148,171,0.07) 0%, transparent 40%)",
        }}
      />

      <div
        className="relative w-full max-w-md p-8 rounded-2xl auth-arc"
        style={{
          background: "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
          border: "1px solid rgba(131,148,171,0.2)",
          boxShadow:
            "0 24px 64px rgba(12,44,84,0.22), 0 4px 16px rgba(12,44,84,0.12), inset 0 1px 0 rgba(131,148,171,0.12)",
        }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div
              style={{
                width: "72px", height: "72px", borderRadius: "14px", overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.18)",
                padding: "6px",
              }}
            >
              <img src="/assets/logo.png" alt="Axo Signal logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
          </div>

          {!invalidLink && status !== "done" && (
            <>
              <p style={{ fontFamily: DM_SANS, color: "#ffffff", fontSize: "16px", fontWeight: "400", marginTop: "0.5rem" }}>
                Choose a new password
              </p>
              <p style={{ fontFamily: DM_SANS, color: "rgba(255,255,255,0.55)", fontSize: "13px", marginTop: "0.35rem", lineHeight: "1.5" }}>
                Must be at least 8 characters.
              </p>
            </>
          )}
        </div>

        {/* ── INVALID / MISSING TOKEN ── */}
        {invalidLink && (
          <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
            <div style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M6 18L18 6M6 6l12 12" stroke="#f87171" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </div>
            <p style={{ fontFamily: DM_SANS, color: "#ffffff", fontSize: "16px", fontWeight: "600", marginBottom: "8px" }}>
              Invalid reset link
            </p>
            <p style={{ fontFamily: DM_SANS, color: "rgba(255,255,255,0.55)", fontSize: "13px", lineHeight: "1.6", marginBottom: "24px" }}>
              This link is missing a token. Please request a new password reset link.
            </p>
            <Link to="/forgot-password"
              style={{
                fontFamily: DM_SANS, fontSize: "14px", fontWeight: "600",
                color: "#0c2c54", background: "#ffffff", padding: "10px 24px",
                borderRadius: "8px", textDecoration: "none", display: "inline-block",
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              }}
            >
              Request new link
            </Link>
          </div>
        )}

        {/* ── SUCCESS STATE ── */}
        {!invalidLink && status === "done" && (
          <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
            <div style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#4ade80" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p style={{ fontFamily: DM_SANS, color: "#ffffff", fontSize: "16px", fontWeight: "600", marginBottom: "8px" }}>
              Password updated!
            </p>
            <p style={{ fontFamily: DM_SANS, color: "rgba(255,255,255,0.55)", fontSize: "13px", lineHeight: "1.6", marginBottom: "28px" }}>
              Your password has been reset. You can now sign in with your new credentials.
            </p>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() => navigate("/login")}
              style={{ fontFamily: DM_SANS, fontWeight: 600, paddingInline: 24 }}
            >
              Sign In
            </Button>
          </div>
        )}

        {/* ── FORM ── */}
        {!invalidLink && status !== "done" && (
          <>
            {error && (
              <div className="mb-5 p-3 rounded-lg text-sm" style={{
                background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)",
                color: "#fca5a5", fontFamily: DM_SANS,
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New password */}
              <PasswordStrength
                label="New Password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                rules={AXO_PASSWORD_RULES}
                required
                minLength={8}
                placeholder="••••••••"
                disabled={status === "loading"}
              />

              {/* Confirm password */}
              <PasswordField
                label="Confirm Password"
                id="confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="••••••••"
                disabled={status === "loading"}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={status === "loading"}
                style={{ width: "100%", fontFamily: DM_SANS, fontWeight: 600 }}
              >
                {status === "loading" ? "Updating password..." : "Set New Password"}
              </Button>
            </form>
          </>
        )}

        {/* Footer */}
        <div className="mt-7 pt-6 text-center" style={{ borderTop: "1px solid rgba(92,113,143,0.3)" }}>
          <p style={{ fontFamily: DM_SANS, fontSize: "14px", color: "#ffffff" }}>
            <Link to="/login" style={{ color: "#ffffff", fontWeight: "600" }}
              onMouseEnter={(e) => (e.target.style.color = "rgba(255,255,255,0.7)")}
              onMouseLeave={(e) => (e.target.style.color = "#ffffff")}
            >
              ← Back to Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;